/**
 * Consent Confirmation Page
 *
 * Public page shown when a contact clicks a double opt-in confirmation link.
 * Auto-confirms on mount — no intermediate button required.
 * URL pattern: /confirm-consent/:token
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Skeleton } from '@respectly/ui';
import {
  ShieldCheck,
  Mail,
  MessageSquare,
  Clock,
  AlertCircle,
  CheckCircle2,
  Shield,
} from 'lucide-react';

type PageState = 'loading' | 'success' | 'already_confirmed' | 'expired' | 'invalid';
type Channel = 'email' | 'sms';

interface ConfirmData {
  channel: Channel;
  tenantName: string;
}

const CHANNEL_LABEL: Record<Channel, string> = {
  email: 'Email',
  sms: 'SMS',
};

const CHANNEL_DESCRIPTION: Record<Channel, string> = {
  email: 'email communications',
  sms: 'SMS text messages',
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ConsentConfirmPage() {
  const [state, setState] = useState<PageState>('loading');
  const [data, setData] = useState<ConfirmData | null>(null);
  const [tenantName, setTenantName] = useState<string>('');

  const token = window.location.pathname.split('/confirm-consent/')[1];
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  useEffect(() => {
    if (!token || token.length < 16) {
      setState('invalid');
      return;
    }

    const confirm = async () => {
      try {
        const response = await fetch(`${apiBase}/public/consent-confirm/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        const json = await response.json() as { success: boolean; data?: ConfirmData; error?: { code?: string } };

        if (response.ok && json.success && json.data) {
          setData(json.data);
          setTenantName(json.data.tenantName);
          setState('success');
          return;
        }

        const code = json.error?.code?.toLowerCase() ?? '';
        if (response.status === 409 || code === 'already_confirmed') {
          setState('already_confirmed');
        } else if (response.status === 410 || code === 'expired') {
          setState('expired');
        } else {
          setState('invalid');
        }
      } catch {
        setState('invalid');
      }
    };

    confirm();
  }, [apiBase, token]);

  return (
    <PageShell>
      <AnimatePresence mode="wait">
        {state === 'loading' && <LoadingState key="loading" />}
        {state === 'success' && data && <SuccessState key="success" data={data} />}
        {state === 'already_confirmed' && <AlreadyConfirmedState key="already" tenantName={tenantName} />}
        {state === 'expired' && <ExpiredState key="expired" tenantName={tenantName} />}
        {state === 'invalid' && <InvalidState key="invalid" />}
      </AnimatePresence>
    </PageShell>
  );
}

// ─── States ───────────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="confirmation-card"
    >
      <div className="loading-content">
        <div className="loading-icon-ring">
          <div className="loading-spinner" />
        </div>
        <Skeleton className="skeleton-title" />
        <Skeleton className="skeleton-line" />
        <Skeleton className="skeleton-line-short" />
      </div>
    </motion.div>
  );
}

function SuccessState({ data }: { data: ConfirmData }) {
  const ChannelIcon = data.channel === 'email' ? Mail : MessageSquare;
  const channelLabel = CHANNEL_LABEL[data.channel];
  const channelDesc = CHANNEL_DESCRIPTION[data.channel];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Hero success card */}
      <div className="success-card">
        {/* Decorative corner accents */}
        <div className="corner corner-tl" />
        <div className="corner corner-tr" />
        <div className="corner corner-bl" />
        <div className="corner corner-br" />

        {/* Animated icon */}
        <motion.div
          className="success-icon-wrap"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div className="success-icon-ring">
            <ShieldCheck className="success-icon" />
          </div>
          {/* Radiating rings */}
          <motion.div
            className="success-ring success-ring-1"
            initial={{ scale: 0.8, opacity: 0.6 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{ delay: 0.4, duration: 1.2, ease: 'easeOut' }}
          />
          <motion.div
            className="success-ring success-ring-2"
            initial={{ scale: 0.8, opacity: 0.4 }}
            animate={{ scale: 2.2, opacity: 0 }}
            transition={{ delay: 0.6, duration: 1.4, ease: 'easeOut' }}
          />
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <p className="success-eyebrow">Confirmation Complete</p>
          <h1 className="success-heading">Opt-In Confirmed</h1>
        </motion.div>

        {/* Channel badge */}
        <motion.div
          className="channel-badge-wrap"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.45, duration: 0.4 }}
        >
          <div className="channel-badge">
            <ChannelIcon className="channel-badge-icon" />
            <span>{channelLabel} Opt-In Confirmed</span>
          </div>
        </motion.div>

        {/* Description */}
        <motion.p
          className="success-description"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.4 }}
        >
          You're now subscribed to {channelDesc} from{' '}
          <strong className="tenant-name">{data.tenantName}</strong>.
        </motion.p>

        {/* What this means */}
        <motion.div
          className="info-block"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.4 }}
        >
          <p className="info-block-label">What this means</p>
          <ul className="info-list">
            <li>
              <CheckCircle2 className="info-check" />
              <span>
                Your {channelLabel.toLowerCase()} consent has been securely recorded
              </span>
            </li>
            <li>
              <CheckCircle2 className="info-check" />
              <span>
                {data.tenantName} may send you {channelDesc}
              </span>
            </li>
            <li>
              <CheckCircle2 className="info-check" />
              <span>You can opt out at any time by contacting {data.tenantName}</span>
            </li>
          </ul>
        </motion.div>

        {/* Close note */}
        <motion.p
          className="close-note"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          You may safely close this page.
        </motion.p>
      </div>
    </motion.div>
  );
}

function AlreadyConfirmedState({ tenantName }: { tenantName: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="status-card status-card--blue">
        <div className="status-icon-wrap status-icon-wrap--blue">
          <CheckCircle2 className="status-icon" />
        </div>
        <h1 className="status-heading">Already Confirmed</h1>
        <p className="status-description">
          This opt-in has already been confirmed
          {tenantName ? (
            <> for <strong>{tenantName}</strong></>
          ) : null}.
          No further action is needed.
        </p>
        <div className="status-divider" />
        <p className="close-note">You may safely close this page.</p>
      </div>
    </motion.div>
  );
}

function ExpiredState({ tenantName }: { tenantName: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="status-card status-card--amber">
        <div className="status-icon-wrap status-icon-wrap--amber">
          <Clock className="status-icon" />
        </div>
        <h1 className="status-heading">Confirmation Link Expired</h1>
        <p className="status-description">
          This confirmation link has expired and is no longer valid.
        </p>
        {tenantName && (
          <div className="help-block">
            <p className="help-block-label">What to do next</p>
            <p className="help-block-text">
              Please contact <strong>{tenantName}</strong> and ask them to send you a new
              confirmation link.
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function InvalidState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="status-card status-card--red">
        <div className="status-icon-wrap status-icon-wrap--red">
          <AlertCircle className="status-icon" />
        </div>
        <h1 className="status-heading">Invalid Link</h1>
        <p className="status-description">
          This confirmation link is not valid or has already been used. If you believe
          this is an error, please contact the organization that sent you this link.
        </p>
      </div>
    </motion.div>
  );
}

// ─── Shell ────────────────────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <style>{STYLES}</style>
      <div className="page-shell">
        {/* Header */}
        <header className="page-header">
          <div className="header-inner">
            <div className="logo-mark" aria-label="Respectly" />
            <span className="logo-wordmark">Respectly</span>
          </div>
        </header>

        {/* Main */}
        <main className="page-main">
          <div className="page-content">
            {children}

            {/* Security badge */}
            <div className="security-badge">
              <Shield className="security-icon" />
              <span>Secure &amp; encrypted</span>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="page-footer">
          <p>Powered by Respectly</p>
        </footer>
      </div>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap');

  :root {
    --success-green: #16a34a;
    --success-light: #dcfce7;
    --success-mid: #86efac;
    --blue-600: #2563eb;
    --blue-100: #dbeafe;
    --amber-500: #f59e0b;
    --amber-100: #fef3c7;
    --red-500: #ef4444;
    --red-100: #fee2e2;
    --slate-900: #0f172a;
    --slate-700: #334155;
    --slate-500: #64748b;
    --slate-300: #cbd5e1;
    --slate-100: #f1f5f9;
    --slate-50: #f8fafc;
    --white: #ffffff;
    --font-display: 'Lora', Georgia, serif;
    --font-body: 'DM Sans', system-ui, sans-serif;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* Shell */
  .page-shell {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: var(--slate-50);
    font-family: var(--font-body);
    background-image:
      radial-gradient(ellipse 80% 50% at 50% -20%, rgba(22, 163, 74, 0.06), transparent);
  }

  /* Header */
  .page-header {
    border-bottom: 1px solid var(--slate-300);
    background: var(--white);
  }
  .header-inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 1.5rem;
    height: 3.5rem;
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }
  .logo-mark {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
    flex-shrink: 0;
  }
  .logo-wordmark {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.125rem;
    color: var(--slate-900);
    letter-spacing: -0.01em;
  }

  /* Main */
  .page-main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
  }
  .page-content {
    width: 100%;
    max-width: 28rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  /* Footer */
  .page-footer {
    border-top: 1px solid var(--slate-300);
    background: var(--white);
    padding: 1rem;
    text-align: center;
    font-size: 0.75rem;
    color: var(--slate-500);
  }

  /* Security badge */
  .security-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    font-size: 0.8125rem;
    color: var(--slate-500);
  }
  .security-icon {
    width: 0.875rem;
    height: 0.875rem;
  }

  /* ── Loading ── */
  .confirmation-card {
    background: var(--white);
    border: 1px solid var(--slate-300);
    border-radius: 1rem;
    padding: 2.5rem;
  }
  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  .loading-icon-ring {
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    background: var(--slate-100);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .loading-spinner {
    width: 1.75rem;
    height: 1.75rem;
    border: 2px solid var(--slate-300);
    border-top-color: var(--success-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .skeleton-title {
    height: 1.5rem;
    width: 12rem;
    border-radius: 0.5rem;
    background: var(--slate-100);
    animation: pulse 1.5s ease-in-out infinite;
  }
  .skeleton-line {
    height: 0.875rem;
    width: 16rem;
    border-radius: 0.375rem;
    background: var(--slate-100);
    animation: pulse 1.5s ease-in-out infinite;
  }
  .skeleton-line-short {
    height: 0.875rem;
    width: 10rem;
    border-radius: 0.375rem;
    background: var(--slate-100);
    animation: pulse 1.5s ease-in-out infinite 0.2s;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  /* ── Success Card ── */
  .success-card {
    position: relative;
    background: var(--white);
    border: 1px solid var(--slate-300);
    border-radius: 1rem;
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1.25rem;
    overflow: hidden;
  }
  .success-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--success-green), #4ade80, var(--success-green));
    background-size: 200% 100%;
    animation: shimmer 3s linear infinite;
  }
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* Corner accents */
  .corner {
    position: absolute;
    width: 1.25rem;
    height: 1.25rem;
  }
  .corner-tl { top: 0.75rem; left: 0.75rem; border-top: 1.5px solid var(--success-mid); border-left: 1.5px solid var(--success-mid); border-radius: 2px 0 0 0; }
  .corner-tr { top: 0.75rem; right: 0.75rem; border-top: 1.5px solid var(--success-mid); border-right: 1.5px solid var(--success-mid); border-radius: 0 2px 0 0; }
  .corner-bl { bottom: 0.75rem; left: 0.75rem; border-bottom: 1.5px solid var(--success-mid); border-left: 1.5px solid var(--success-mid); border-radius: 0 0 0 2px; }
  .corner-br { bottom: 0.75rem; right: 0.75rem; border-bottom: 1.5px solid var(--success-mid); border-right: 1.5px solid var(--success-mid); border-radius: 0 0 2px 0; }

  /* Success icon */
  .success-icon-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.5rem;
  }
  .success-icon-ring {
    position: relative;
    z-index: 1;
    width: 5rem;
    height: 5rem;
    border-radius: 50%;
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 6px rgba(22, 163, 74, 0.08);
  }
  .success-icon {
    width: 2.25rem;
    height: 2.25rem;
    color: var(--success-green);
    stroke-width: 1.75;
  }
  .success-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1.5px solid rgba(22, 163, 74, 0.3);
  }

  /* Success text */
  .success-eyebrow {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--success-green);
    margin-bottom: 0.25rem;
  }
  .success-heading {
    font-family: var(--font-display);
    font-size: 1.75rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--slate-900);
    line-height: 1.2;
  }
  .success-description {
    font-size: 0.9375rem;
    color: var(--slate-700);
    line-height: 1.6;
    max-width: 22rem;
  }
  .tenant-name {
    color: var(--slate-900);
    font-weight: 600;
  }

  /* Channel badge */
  .channel-badge-wrap { margin: 0.25rem 0; }
  .channel-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--success-light);
    border: 1px solid var(--success-mid);
    border-radius: 999px;
    padding: 0.375rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #15803d;
  }
  .channel-badge-icon {
    width: 0.875rem;
    height: 0.875rem;
  }

  /* Info block */
  .info-block {
    background: var(--slate-50);
    border: 1px solid var(--slate-300);
    border-radius: 0.75rem;
    padding: 1.125rem 1.25rem;
    text-align: left;
    width: 100%;
  }
  .info-block-label {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--slate-500);
    margin-bottom: 0.75rem;
  }
  .info-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }
  .info-list li {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    font-size: 0.875rem;
    color: var(--slate-700);
    line-height: 1.5;
  }
  .info-check {
    width: 0.9375rem;
    height: 0.9375rem;
    color: var(--success-green);
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  /* Close note */
  .close-note {
    font-size: 0.8125rem;
    color: var(--slate-500);
    margin-top: 0.25rem;
  }

  /* ── Status cards (already, expired, invalid) ── */
  .status-card {
    background: var(--white);
    border: 1px solid var(--slate-300);
    border-radius: 1rem;
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
  }

  .status-icon-wrap {
    width: 4.5rem;
    height: 4.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .status-icon-wrap--blue { background: var(--blue-100); }
  .status-icon-wrap--amber { background: var(--amber-100); }
  .status-icon-wrap--red { background: var(--red-100); }

  .status-icon {
    width: 2rem;
    height: 2rem;
  }
  .status-card--blue .status-icon { color: var(--blue-600); }
  .status-card--amber .status-icon { color: var(--amber-500); }
  .status-card--red .status-icon { color: var(--red-500); }

  .status-heading {
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--slate-900);
    letter-spacing: -0.015em;
  }
  .status-description {
    font-size: 0.9375rem;
    color: var(--slate-700);
    line-height: 1.65;
    max-width: 22rem;
  }
  .status-divider {
    width: 3rem;
    height: 1px;
    background: var(--slate-300);
    margin: 0.25rem 0;
  }

  /* Help block (expired state) */
  .help-block {
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    text-align: left;
    width: 100%;
  }
  .help-block-label {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #92400e;
    margin-bottom: 0.5rem;
  }
  .help-block-text {
    font-size: 0.875rem;
    color: #78350f;
    line-height: 1.55;
  }
`;

export default ConsentConfirmPage;
